import React, { createContext, useContext, useState } from "react";
import { ProbateField } from "./models/ProbateField";
import { isProd, STORAGE } from "./constants";
import { useAuthContext } from "./AuthContextWrapper";
import { ProbateDetails } from "models/ProbateDetails";
import { probateApi } from "apis";

interface Props {
  children: JSX.Element;
}

interface ProbateContextInterface {
  saveFieldAnsweredInLocalStorage: (field: ProbateField) => void;
  getFieldsAnsweredFromLocalStorage: () => ProbateField[] | undefined;
  storeFieldAnswered: (field:ProbateField) => Promise<void>;
  clearFieldsAnswered: () => void;
  getProbateDetails: () => Promise<ProbateDetails|undefined>;
  getFieldAnswerForUser: (userId:number, fieldId:string) => Promise<string|boolean|number|undefined>
}

const ProbateContext = createContext<ProbateContextInterface | null>(null);

export const ProbateContextWrapper = ({ children }: Props): JSX.Element => {
  const { user, activeEstate } = useAuthContext()
  const [fieldsAnswered, setFieldsAnswered] = useState<ProbateField[]>([]);
  const fieldsAnsweredStorageItem = `${STORAGE.fieldsAnswered}-${user?.id}-${activeEstate?.stateId}`;

  const getProbateDetails = async (): Promise<ProbateDetails|undefined> => {
    if(!activeEstate || !activeEstate.id || !user?.id) {
      !isProd && console.warn("we need lead data before we can get probate details/fields")
      return
    }

    return await probateApi.getProbateDetails(activeEstate.id, user.id)
  };

  const getFieldsAnsweredFromLocalStorage = (): ProbateField[] => {
    let fields: ProbateField[] = [];
    const savedFieldsAnswered = window.localStorage.getItem(
      fieldsAnsweredStorageItem
    );
    if (savedFieldsAnswered) {
      fields = JSON.parse(savedFieldsAnswered)?.map((object: any) =>
        ProbateField.fromStringified(object)
      );
      setFieldsAnswered(fields);
    }
    return fields;
  };

  const saveFieldAnsweredInLocalStorage = (probateField: ProbateField): void => {
    const foundField = fieldsAnswered.find((answeredField: ProbateField) => {
      return answeredField.id === probateField.id;
    });
    if (!foundField) {
      const newFieldsAnswered = [...fieldsAnswered, probateField];
      setFieldsAnswered(newFieldsAnswered);
      window.localStorage.setItem(
        fieldsAnsweredStorageItem,
        JSON.stringify(newFieldsAnswered)
      );
    }
  };

  const storeFieldAnswered = async (probateField: ProbateField): Promise<void> => {
    if(user?.id) {
      await probateApi.saveFieldToKnowledgeEngine(user?.id, probateField, true)
    }
  }

  const storeFieldAnsweredForOtherUser = async (otherUserId:number, probateField: ProbateField): Promise<void> => {
    await probateApi.saveFieldToKnowledgeEngine(otherUserId, probateField, true)
  }

  const clearFieldsAnswered = (clearStorage = true): void => {
    setFieldsAnswered([]);
    clearStorage && window.localStorage.removeItem(fieldsAnsweredStorageItem);
  };

  const getFieldAnswerForUser = async (userId:number,fieldId:string):Promise<string|boolean|number|undefined> => {
    return await probateApi.getFieldAnswerForUser(userId, fieldId)
  } 

  const getFieldAnswerForOtherUser = async (otherUserId:number, fieldId:string):Promise<string|boolean|number|undefined> => {
    return await probateApi.getFieldAnswerForUser(otherUserId, fieldId)
  } 

  return (
    <ProbateContext.Provider
      value={{
        saveFieldAnsweredInLocalStorage,
        getFieldsAnsweredFromLocalStorage,
        clearFieldsAnswered,
        getProbateDetails,
        storeFieldAnswered,
        getFieldAnswerForUser,
      }}
    >
      {children}
    </ProbateContext.Provider>
  );
};

export function useProbateContext():ProbateContextInterface {
  const probateContext = useContext(ProbateContext)
  if(!probateContext) {
    throw new Error("the component calling this probate context function is not nested within the context provider")
  }

  return probateContext
}
